/**
 * TokenManager - Production-grade token lifecycle management
 * 
 * Responsibilities:
 * - Secure token storage (sessionStorage)
 * - Automatic token refresh with race condition prevention
 * - Token expiration handling
 * - Secure token cleanup
 */

interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user_id?: string;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user?: {
    id: string;
    email?: string;
    [key: string]: unknown;
  };
}

export class TokenManager {
  private static readonly TOKEN_STORAGE_KEY = 'cube-test-auth-tokens';
  private static readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
  private static refreshPromise: Promise<string | null> | null = null;

  /**
   * Secure storage interface - uses localStorage for persistent authentication
   * Tokens persist across page refreshes and browser sessions
   */
  private static get storage(): Storage | null {
    if (typeof window === 'undefined') return null;
    return localStorage;
  }

  /**
   * Store authentication tokens securely
   */
  static storeTokens(session: RefreshResponse): void {
    if (!this.storage) {
      console.error('🚨 TokenManager: Storage not available');
      return;
    }

    const tokenData: TokenData = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: Date.now() + (session.expires_in * 1000),
      user_id: session.user?.id
    };

    try {
      this.storage.setItem(this.TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
      console.log('💾 TokenManager: Tokens stored securely', {
        expires_at: new Date(tokenData.expires_at).toISOString(),
        user_id: tokenData.user_id
      });
    } catch (error) {
      console.error('🚨 TokenManager: Failed to store tokens:', error);
      throw new Error('Token storage failed');
    }
  }

  /**
   * Retrieve stored tokens
   */
  private static getStoredTokens(): TokenData | null {
    if (!this.storage) return null;

    try {
      const stored = this.storage.getItem(this.TOKEN_STORAGE_KEY);
      if (!stored) return null;

      const tokenData = JSON.parse(stored) as TokenData;
      
      // Validate token structure
      if (!tokenData.access_token || !tokenData.refresh_token || !tokenData.expires_at) {
        console.warn('⚠️ TokenManager: Invalid token data structure, clearing storage');
        this.clearTokens();
        return null;
      }

      return tokenData;
    } catch (error) {
      console.error('🚨 TokenManager: Failed to parse stored tokens:', error);
      this.clearTokens();
      return null;
    }
  }

  /**
   * Clear all stored tokens securely
   */
  static clearTokens(): void {
    if (!this.storage) return;

    try {
      this.storage.removeItem(this.TOKEN_STORAGE_KEY);
      console.log('🗑️ TokenManager: Tokens cleared from secure storage');
    } catch (error) {
      console.error('🚨 TokenManager: Failed to clear tokens:', error);
    }

    // Also clear any ongoing refresh
    this.refreshPromise = null;
  }

  /**
   * Check if user has valid authentication tokens
   */
  static isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    return !!tokens?.access_token;
  }

  /**
   * Get the current user ID from stored tokens
   */
  static getCurrentUserId(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.user_id || null;
  }

  /**
   * Check if current token needs refresh
   */
  private static needsRefresh(tokens: TokenData): boolean {
    return tokens.expires_at - Date.now() < this.REFRESH_BUFFER_MS;
  }

  /**
   * Get a valid access token, refreshing if necessary
   * This is the main method other services should use
   */
  static async getValidAccessToken(): Promise<string | null> {
    const tokens = this.getStoredTokens();
    if (!tokens) {
      console.log('ℹ️ TokenManager: No tokens available');
      return null;
    }

    // Return current token if still valid
    if (!this.needsRefresh(tokens)) {
      return tokens.access_token;
    }

    // Token needs refresh
    console.log('🔄 TokenManager: Token expires soon, refreshing...');
    return await this.refreshAccessToken();
  }

  /**
   * Refresh access token with race condition prevention
   */
  private static async refreshAccessToken(): Promise<string | null> {
    // Prevent concurrent refresh attempts
    if (this.refreshPromise) {
      console.log('🔄 TokenManager: Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    const tokens = this.getStoredTokens();
    if (!tokens?.refresh_token) {
      console.error('🚨 TokenManager: No refresh token available');
      return null;
    }

    // Create refresh promise to prevent race conditions
    this.refreshPromise = this.performTokenRefresh(tokens.refresh_token);

    try {
      const result = await this.refreshPromise;
      return result;
    } catch (error) {
      console.error('🚨 TokenManager: Token refresh failed:', error);
      return null;
    } finally {
      // Clear promise regardless of success/failure
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh API call
   */
  private static async performTokenRefresh(refreshToken: string): Promise<string | null> {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://placeholder.supabase.co';
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || 'placeholder-key';

    try {
      console.log('🔄 TokenManager: Performing token refresh API call...');
      
      const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🚨 TokenManager: Refresh request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });

        // Clear tokens on auth failure (invalid refresh token)
        if (response.status === 400 || response.status === 401) {
          console.log('🗑️ TokenManager: Invalid refresh token, clearing all tokens');
          this.clearTokens();
        }

        return null;
      }

      const refreshData: RefreshResponse = await response.json();
      
      // Validate refresh response
      if (!refreshData.access_token || !refreshData.refresh_token) {
        console.error('🚨 TokenManager: Invalid refresh response structure');
        return null;
      }

      // Store new tokens
      this.storeTokens(refreshData);
      
      console.log('✅ TokenManager: Token refreshed successfully');
      return refreshData.access_token;

    } catch (error) {
      console.error('🚨 TokenManager: Network error during token refresh:', error);
      
      // Don't clear tokens on network errors - might be temporary
      // Let the calling code decide how to handle this
      throw error;
    }
  }

  /**
   * Revoke tokens on the server (for secure logout)
   */
  static async revokeTokens(): Promise<boolean> {
    const tokens = this.getStoredTokens();
    if (!tokens?.refresh_token) {
      console.log('ℹ️ TokenManager: No tokens to revoke');
      return true;
    }

    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
          'apikey': supabaseKey,
        }
      });

      if (response.ok) {
        console.log('✅ TokenManager: Tokens revoked on server');
        return true;
      } else {
        console.warn('⚠️ TokenManager: Server token revocation failed, but continuing with local cleanup');
        return false;
      }
    } catch (error) {
      console.error('🚨 TokenManager: Error revoking tokens on server:', error);
      return false;
    } finally {
      // Always clear local tokens regardless of server response
      this.clearTokens();
    }
  }
}